import React, { useMemo, useState } from "react";
import { flushSync } from "react-dom";
import {
  useBanUsersMutation,
  useUnbanUsersMutation,
} from "../../store/services/users";
import {
  setBanned,
  setEmail,
  setFirstName,
  setId,
  setLastName,
  setTenantId as setUsersFilterTenantId,
} from "../../store/slices/usersFiltersSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux";
import { useDebouncedUsersQuery } from "../../hooks/useDebouncedUsersQuery";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { DataTable, DataTableFilterMeta, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import {
  DateBodyTemplate,
  UserPhoneBodyTemplate,
  TenantBodyTemplate,
} from "../../components";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { User } from "../../models/users";
import { getBanStatusLabel, isUserRestrictedByBan } from "../../utils/functions/users";
import {
  getApiErrorMessage,
  getApiValidationIssueFields,
  hasApiValidationIssues,
} from "../../utils/functions/api-errors";

const getFriendlyBanErrorMessage = (error: unknown): string => {
  const defaultMessage = "No se pudo actualizar el estado de ban del usuario";
  const errorMessage = getApiErrorMessage(error);
  const issueFields = getApiValidationIssueFields(error);

  if (issueFields.length > 0) {
    return `${errorMessage || "Falló la validación"}: ${issueFields.join(", ")}`;
  }

  if (hasApiValidationIssues(error)) {
    return errorMessage || "Hay campos obligatorios incompletos para actualizar el usuario";
  }

  return errorMessage || defaultMessage;
};

const getBooleanFilterValue = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined;

const getTenantFilterValue = (value: unknown): string | null | undefined =>
  typeof value === "string" || value === null ? value : undefined;

const defaultFakeUsersFilters: DataTableFilterMeta = {
  email: { value: null, matchMode: "contains" },
  lastName: { value: null, matchMode: "contains" },
  firstName: { value: null, matchMode: "contains" },
};

const getTextFilterValue = (
  filters: DataTableFilterMeta,
  field: string
): string | undefined => {
  const filterMeta = filters[field];

  if (!filterMeta) return undefined;

  if ("value" in filterMeta) {
    return typeof filterMeta.value === "string" ? filterMeta.value : undefined;
  }

  if ("constraints" in filterMeta) {
    const firstConstraint = filterMeta.constraints?.[0];
    return typeof firstConstraint?.value === "string"
      ? firstConstraint.value
      : undefined;
  }

  return undefined;
};

export const Users = () => {
  const dispatch = useAppDispatch();
  const {
    id: userId,
    email: userEmail,
    firstName: userFirstName,
    lastName: userLastName,
    banned: userBanned,
    tenantId: userTenantId,
  } = useAppSelector((state) => state.usersFilters);
  const [userToBan, setUserToBan] = useState<User>();
  const [showBanDialog, setShowBanDialog] = useState<boolean>(false);
  const [banReason, setBanReason] = useState<string>("");
  const [fakeUsersPage, setFakeUsersPage] = useState<number>(0);
  const [fakeUsersRows, setFakeUsersRows] = useState<number>(5);
  const [fakeUsersFilters, setFakeUsersFilters] =
    useState<DataTableFilterMeta>(defaultFakeUsersFilters);
  const navigate = useNavigate();
  
  // Fetch tenants for dropdown
  const { data: tenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});

  const tenantOptions = React.useMemo(() => {
    return Array.isArray(tenants) ? tenants : [];
  }, [tenants]);

  const realUsersQueryArgs = useMemo(() => {
    const base = { userTypeId: 2 as const };
    const hasSearchFilters = Boolean(
      userId.trim() ||
      userEmail ||
      userFirstName ||
      userLastName ||
      userTenantId !== undefined ||
      userBanned !== undefined
    );

    if (!hasSearchFilters) {
      return base;
    }

    return {
      ...base,
      id: userId,
      email: userEmail,
      firstName: userFirstName,
      lastName: userLastName,
      tenantId: userTenantId || undefined,
      ...(userBanned !== undefined ? { banned: userBanned } : {}),
    };
  }, [userId, userEmail, userFirstName, userLastName, userTenantId, userBanned]);

  const fakeUsersQueryArgs = useMemo(
    () => ({
      userTypeId: 3 as const,
      limit: String(fakeUsersRows),
      page: fakeUsersPage + 1,
      email: getTextFilterValue(fakeUsersFilters, "email"),
      lastName: getTextFilterValue(fakeUsersFilters, "lastName"),
      firstName: getTextFilterValue(fakeUsersFilters, "firstName"),
    }),
    [fakeUsersPage, fakeUsersRows, fakeUsersFilters]
  );

  const {
    data: usersData,
    isLoading,
    refetch,
  } = useDebouncedUsersQuery(realUsersQueryArgs);

  const {
    data: fakeUsersData,
    pagination: fakeUsersPagination,
    isLoading: isLoadingFakeUsers,
  } = useDebouncedUsersQuery(fakeUsersQueryArgs);
  const [banUsers, { isLoading: isBanningUser }] = useBanUsersMutation();
  const [unbanUsers, { isLoading: isUnbanningUser }] = useUnbanUsersMutation();
  const isUpdatingUser = isBanningUser || isUnbanningUser;

  const hideBanUserDialog = () => {
    setShowBanDialog(false);
    setUserToBan(undefined);
    setBanReason("");
  };

  const handleBanUser = async () => {
    if (!userToBan) return;

    const isBanningAction = !isUserRestrictedByBan(userToBan);

    if (isBanningAction && !banReason.trim()) {
      toast.error("Debe ingresar un motivo para el baneo");
      return;
    }

    try {
      if (isBanningAction) {
        await banUsers({
          userIds: [userToBan.id],
          banReason: banReason.trim(),
        }).unwrap();
      } else {
        await unbanUsers({
          userIds: [userToBan.id],
        }).unwrap();
      }

      toast.success(
        isBanningAction
          ? `Usuario ${userToBan.firstName} ${userToBan.lastName} baneado exitosamente!`
          : `Usuario ${userToBan.firstName} ${userToBan.lastName} desbaneado exitosamente!`
      );
      hideBanUserDialog();
    } catch (e: unknown) {
      toast.error(getFriendlyBanErrorMessage(e));
    }
  };

  const formatOptionalDate = (dateValue?: string) => {
    if (!dateValue) return "-";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  };

  const isRowRestricted = (rowData: User) => isUserRestrictedByBan(rowData);

  const formatResponsible = (value?: string) => {
    if (!value) return "-";
    const cleaned = value.trim();
    if (!cleaned || cleaned.toLowerCase() === "unknown") return "-";
    if (!cleaned.includes("@")) return "-";
    return cleaned;
  };

  const getBanStatusByRow = (rowData: User) => {
    return isRowRestricted(rowData) ? "REVERSIBLE" : "NONE";
  };

  const openBanDialog = (rowData: User) => {
    flushSync(() => {
      setBanReason("");
      setUserToBan(rowData);
    });
    setShowBanDialog(true);
  };

  const RealUsersheader = (
    <div className="grid mt-4">
      <div className="col-12 md:col-2 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="id"
            name="id"
            onChange={(e) => {
              const rawValue = e.currentTarget.value;
              const numericOnly = rawValue.replace(/\D+/g, "");
              dispatch(setId(numericOnly));
            }}
            value={userId}
            inputMode="numeric"
            pattern="[0-9]*"
          />
          <label htmlFor="id">ID</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => dispatch(setId(""))}
        />
      </div>
      <div className="col-12 md:col-4 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="email"
            name="email"
            onChange={(e) => dispatch(setEmail(e.currentTarget.value))}
            value={userEmail}
          />
          <label htmlFor="email">Email</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => dispatch(setEmail(""))}
        />
      </div>
      <div className="col-12 md:col-3 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="lastName"
            name="lastName"
            onChange={(e) => dispatch(setLastName(e.currentTarget.value))}
            value={userLastName}
          />
          <label htmlFor="lastName">Apellido</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => dispatch(setLastName(""))}
        />
      </div>
      <div className="col-12 md:col-3 p-inputgroup">
        <span className="p-float-label">
          <InputText
            className="w-full"
            id="firstName"
            name="firstName"
            onChange={(e) => dispatch(setFirstName(e.currentTarget.value))}
            value={userFirstName}
          />
          <label htmlFor="firstName">Nombre</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => dispatch(setFirstName(""))}
        />
      </div>
      <div className="col-12 md:col-3 p-inputgroup">
        <span className="p-float-label">
          <Dropdown
            className="w-full"
            id="userTenantId"
            name="userTenantId"
            onChange={(e) => {
              dispatch(setUsersFilterTenantId(getTenantFilterValue(e.value)));
            }}
            value={userTenantId}
            optionLabel="provider"
            optionValue="id"
            options={tenantOptions}
            loading={isLoadingTenants}
            placeholder="Seleccionar tenant"
            showClear
          />
          <label htmlFor="userTenantId">Tenant</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => dispatch(setUsersFilterTenantId(undefined))}
        />
      </div>
      <div className="col-12 md:col-3 p-inputgroup">
        <span className="p-float-label">
          <Dropdown
            className="w-full"
            id="userBanned"
            name="userBanned"
            onChange={(e) => {
              dispatch(setBanned(getBooleanFilterValue(e.value)));
            }}
            value={userBanned}
            options={[
              { label: "Baneado", value: true },
              { label: "Activo", value: false },
            ]}
            placeholder="Estado"
            showClear
          />
          <label htmlFor="userBanned">Estado</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => dispatch(setBanned(undefined))}
        />
      </div>
    </div>
  );

  const fakeUsersTableHeader = () => (
    <div className="flex flex-column md:flex-row justify-content-between align-items-center">
      <div className="flex align-items-center gap-2">
        <h1 className="text-2xl font-bold m-0 p-0">Lista de usuarios ficticios</h1>
        <Button
          icon="pi pi-plus"
          rounded
          size="small"
          onClick={() => navigate("new")}
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="p-card gap-4 p-3">
        <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-6">
          <h1 className="text-center">Usuarios</h1>
        </div>
        <DataTable
          value={usersData ?? []}
          loading={isLoading}
          header={RealUsersheader}
          tableStyle={{ minWidth: "60rem" }}
          paginator
          rows={5}
          rowsPerPageOptions={[5, 10, 25, 50]}
        >
          <Column field="id" header="ID"></Column>
          <Column field="email" header="Email"></Column>
          <Column field="lastName" header="Apellido"></Column>
          <Column field="firstName" header="Nombre"></Column>
          <Column
            field="userPhone"
            header="Telefono"
            body={UserPhoneBodyTemplate}
          ></Column>
          <Column
            field="createDate"
            header="Fecha de creación"
            body={DateBodyTemplate}
          ></Column>
          <Column
            field="banned"
            header="Ban"
            body={(rowData: User) => (
              <i className={`pi ${isRowRestricted(rowData) ? 'pi-ban text-red-500' : 'pi-check text-green-500'}`} 
                 style={{ fontSize: '1.2rem' }} 
                 title={isRowRestricted(rowData) ? 'Usuario baneado' : 'Usuario activo'} />
            )}
          />
          <Column
            field="banStatus"
            header="Tipo restricción"
            body={(rowData: User) => getBanStatusLabel(getBanStatusByRow(rowData))}
          />
          <Column
            field="banDate"
            header="Fecha restricción"
            body={(rowData: User) => formatOptionalDate(rowData.banDate)}
          />
          <Column
            field="bannedBy"
            header="Responsable"
            body={(rowData: User) => formatResponsible(rowData.bannedBy)}
          />
          <Column
            field="banReason"
            header="Motivo"
            body={(rowData: User) => rowData.banReason || "-"}
          />
          <Column
            header="Acciones"
            body={(rowData: User) => (
              <Button
                label={isRowRestricted(rowData) ? "Desbanear" : "Banear"}
                icon={isRowRestricted(rowData) ? "pi pi-check" : "pi pi-ban"}
                severity={isRowRestricted(rowData) ? "secondary" : "danger"}
                size="small"
                onClick={() => openBanDialog(rowData)}
                disabled={isUpdatingUser}
              />
            )}
          />
          <Column
            field="tenants"
            header="Tenant"
            body={(rowData: User) => (
              <TenantBodyTemplate rowData={rowData} onUpdate={refetch} />
            )}
          />
        </DataTable>
      </div>
      <div className="p-card gap-4 mt-5 p-3">
        <DataTable
          value={fakeUsersData ?? []}
          loading={isLoadingFakeUsers}
          header={fakeUsersTableHeader}
          paginator
          lazy
          first={fakeUsersPage * fakeUsersRows}
          rows={fakeUsersRows}
          totalRecords={fakeUsersPagination?.total ?? 0}
          rowsPerPageOptions={[5, 10, 25, 50]}
          onPage={(event) => {
            setFakeUsersPage(Math.floor(event.first / event.rows));
            setFakeUsersRows(event.rows);
          }}
          filters={fakeUsersFilters}
          onFilter={(event: DataTableStateEvent) => {
            setFakeUsersFilters(event.filters);
            setFakeUsersPage(0);
          }}
          className="my-4"
          filterDisplay="row"
          globalFilterFields={["email", "lastName", "firstName"]}
        >
          <Column
            field="email"
            header="Email"
            filter
            filterPlaceholder="Buscar por email"
            showFilterMenu={false}
            filterMatchMode="contains"
          />
          <Column
            field="lastName"
            header="Apellido"
            filter
            filterPlaceholder="Buscar por apellido"
            showFilterMenu={false}
            filterMatchMode="contains"
          />
          <Column
            field="firstName"
            header="Nombre"
            filter
            filterPlaceholder="Buscar por nombre"
            showFilterMenu={false}
            filterMatchMode="contains"
          />
        </DataTable>
      </div>

      <Dialog
        header={userToBan && isUserRestrictedByBan(userToBan) ? "Desbanear Usuario" : "Banear Usuario (BANEO REVERSIBLE)"}
        visible={showBanDialog}
        style={{ width: "50vw" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        onHide={hideBanUserDialog}
        footer={
          userToBan ? (
            <div>
              <Button
                severity={isUserRestrictedByBan(userToBan) ? "success" : "danger"}
                label={isUserRestrictedByBan(userToBan) ? "Desbanear" : "Banear"}
                icon={isUserRestrictedByBan(userToBan) ? "pi pi-check-circle" : "pi pi-ban"}
                onClick={handleBanUser}
                loading={isUpdatingUser}
                disabled={isUpdatingUser || (!isUserRestrictedByBan(userToBan) && !banReason.trim())}
              />
              <Button
                severity="secondary"
                label="Cancelar"
                onClick={hideBanUserDialog}
                disabled={isUpdatingUser}
              />
            </div>
          ) : null
        }
        appendTo={document.body}
      >
        <div className="mb-3">
          <h3 className={userToBan && isUserRestrictedByBan(userToBan) ? "text-green-500 m-0" : "text-red-500 m-0"}>
            <i className={`pi ${userToBan && isUserRestrictedByBan(userToBan) ? "pi-check-circle" : "pi-exclamation-triangle"} mr-2`}></i>
            {userToBan && isUserRestrictedByBan(userToBan) ? "Desbanear Usuario" : "Banear Usuario (BANEO REVERSIBLE)"}
          </h3>
        </div>
        <p className="mb-3">
          ¿Estás seguro que deseas {userToBan && isUserRestrictedByBan(userToBan) ? "desbanear" : "banear"} al usuario{" "}
          <b>
            {userToBan?.firstName} {userToBan?.lastName}
          </b>
          {userToBan?.email && ` (${userToBan?.email})`}?
        </p>
        {!userToBan || !isUserRestrictedByBan(userToBan) ? (
          <>
            <p className="mb-2 text-600">
              El usuario no podrá acceder a la aplicación mientras esté en BANEO REVERSIBLE y será excluido
              automáticamente de todas las herramientas de comunicación masiva.
            </p>
            <div className="mb-3">
              <label htmlFor="banReason" className="block mb-2 font-medium">
                Motivo del baneo (requerido):
              </label>
              <InputTextarea
                id="banReason"
                value={banReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setBanReason(e.target.value)}
                rows={4}
                className="w-full"
                placeholder="Ingrese el motivo del baneo..."
              />
            </div>
          </>
        ) : (
          <div className="surface-100 border-round p-3">
            <p className="m-0"><b>Tipo actual:</b> {getBanStatusLabel(getBanStatusByRow(userToBan))}</p>
            <p className="m-0 mt-2"><b>Motivo:</b> {userToBan.banReason || "-"}</p>
            <p className="m-0 mt-2"><b>Fecha:</b> {formatOptionalDate(userToBan.banDate)}</p>
            <p className="m-0 mt-2"><b>Administrador:</b> {formatResponsible(userToBan.bannedBy)}</p>
          </div>
        )}
      </Dialog>
    </>
  );
};
